import { forwardRef, Fragment } from 'react'
import type { MutableRefObject, RefObject, ReactNode } from 'react'
import {
  Dialog as HUIDialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
  Description,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { cn } from './cn'

export type DialogSize = 'sm' | 'md' | 'lg'

// title は描画可能な値に限定（null/undefined/boolean を型レベルで排除し、
// 「title 指定なのに無名ダイアログ」になる実行時バグを防ぐ）。
type RenderableTitle = Exclude<ReactNode, null | undefined | boolean>

interface DialogBaseProps {
  /** 開閉状態（外部制御） */
  open: boolean
  /** Escape / オーバーレイクリック / 明示閉じで発火 */
  onClose: () => void
  /** 本文（content） */
  children?: ReactNode
  /** 補足説明（任意。aria-describedby に関連付け） */
  description?: ReactNode
  /** フッタのアクション領域（任意） */
  actions?: ReactNode
  /** 初期フォーカス対象（任意。Headless UI Dialog に委譲）。
   *  useRef<HTMLInputElement>(null) 等をそのまま渡せるよう RefObject<HTMLElement> で受ける。 */
  initialFocus?: RefObject<HTMLElement>
  /** 最大幅。既定 md（既存モーダル max-w-md 相当） */
  size?: DialogSize
  /** パネルの追加クラス（append-only） */
  className?: string
}

// accessible name の強制（型レベル）: title か ariaLabel のどちらか一方を必須にする。
type DialogWithTitle = DialogBaseProps & { title: RenderableTitle; ariaLabel?: never }
type DialogWithAriaLabel = DialogBaseProps & { title?: never; ariaLabel: string }
export type DialogProps = DialogWithTitle | DialogWithAriaLabel

// 最大幅は variant が確定（既存モーダルの max-w-md を md に対応）
const SIZE: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
}

/**
 * モーダル基盤の薄い primitive。
 * focus trap / focus return / scroll lock / Escape / aria-modal は @headlessui/react が担保する。
 * 2 ステップ進行・削除確認等のワークフローは持たない（feature 層に残す）。
 */
export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  { open, onClose, title, ariaLabel, description, actions, initialFocus, size = 'md', className, children },
  ref,
) {
  // 可視タイトルの有無を明示判定（truthy 判定では 0 等の有効値を誤って隠すため）。
  // title は型で null/undefined/boolean を排除済み。文字列の場合は前後空白を除いた空文字も無タイトル扱いとし
  // aria-label にフォールバックする。文字列以外（ReactElement/number/配列等）は可視テキストの有無を静的判定できないため present とみなす。
  const titleIsBlankString = typeof title === 'string' && title.trim() === ''
  const hasTitle = title !== undefined && title !== null && !titleIsBlankString
  const ariaLabelIsBlank = typeof ariaLabel === 'string' && ariaLabel.trim() === ''
  const hasAriaLabel = ariaLabel !== undefined && !ariaLabelIsBlank

  // accessible name の最終解決:
  // - 可視 title がある → DialogTitle が aria-labelledby を担うので aria-label は付けない
  // - title が空/空白で ariaLabel もない → 本番でも無名 dialog を出さないため汎用名でフォールバック
  const FALLBACK_DIALOG_NAME = 'ダイアログ'
  const resolvedAriaLabel = hasTitle ? undefined : hasAriaLabel ? ariaLabel : FALLBACK_DIALOG_NAME

  // 型で防ぎきれない抜け穴（空/空白のみの title かつ ariaLabel が無い/空白のみ）を開発時に検知し、
  // 開発者に修正を促す（本番はフォールバック名で名前を保証しつつクラッシュさせない）。
  // 注: テキストを持たない ReactNode（空要素・空配列等）が visible name を欠くケースは
  //     ReactNode を受け取る以上、静的・実行時とも完全検知は不可能（許容限界）。
  if (import.meta.env.DEV && !hasTitle && !hasAriaLabel) {
    console.error(
      'Dialog: アクセシブルな名前がありません。非空の `title` か `ariaLabel` を指定してください（本番では汎用名でフォールバックします）。',
    )
  }

  return (
    <Transition show={open} as={Fragment}>
      <HUIDialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        // Headless UI は MutableRefObject 型を要求するが current は読み取りのみのため境界で1度キャスト。
        initialFocus={initialFocus as MutableRefObject<HTMLElement | null> | undefined}
        aria-label={resolvedAriaLabel}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                ref={ref}
                className={cn(
                  'w-full bg-card rounded-panel shadow-card max-h-[90vh] overflow-y-auto',
                  SIZE[size],
                  className,
                )}
              >
                {hasTitle ? (
                  <DialogTitle className="px-6 pt-6 text-h3 font-medium text-text-heading">
                    {title}
                  </DialogTitle>
                ) : null}
                {description ? (
                  <Description className="px-6 pt-2 text-text-sub">{description}</Description>
                ) : null}
                <div className="px-6 py-4">{children}</div>
                {actions ? (
                  <div className="flex justify-end gap-3 px-6 pb-6">{actions}</div>
                ) : null}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HUIDialog>
    </Transition>
  )
})
